import { useRef } from 'react';

import { InputLabel, TextField, Typography } from '@mui/material';

import { cssprefix, flattenIntoKey, formatUSTel } from '@/components/DynamicForm/utils/utils';
import { SimpleInput } from '@/components/DynamicForm/types/DynamicFormTypes';
import { publish } from '@/components/DynamicForm/events';

const getCursorPosition = (oldValue: string, newValue: string, currentPos: number): number => {
	const targetDigitCount = oldValue.slice(0, currentPos).replace(/\D/g, '').length;

	const result = [...newValue].reduce<{ digitsSeen: number; position: number }>(
		(acc, char, idx) => {
			if (acc.digitsSeen >= targetDigitCount) return acc;
			const isDigit = /\d/.test(char);
			const newDigitsSeen = acc.digitsSeen + (isDigit ? 1 : 0);
			return {
				digitsSeen: newDigitsSeen,
				position: newDigitsSeen >= targetDigitCount ? idx + 1 : acc.position,
			};
		},
		{ digitsSeen: 0, position: newValue.length }
	);

	return result.position;
};

export default function TelInput({
	label,
	name,
	placeholder,
	value,
	showError,
	errMsg,
	labelSpacing,
	tagData,
	validations,
	reset,
}: SimpleInput) {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target;
		const cursorPos = input.selectionStart ?? 0;
		const oldValue = input.value;
		const rawValue = input.value.replace(/\D+/g, '');

		// Limit to 10 digits
		const limitedValue = rawValue.slice(0, 10);
		const formattedValue = formatUSTel(limitedValue);

		// Update the input value
		input.value = formattedValue;

		// Calculate and set new cursor position
		const newCursorPos = getCursorPosition(oldValue, formattedValue, cursorPos);
		input.setSelectionRange(newCursorPos, newCursorPos);

		// Publish the update with the formatted value
		publish('formDataUpdate', [name, formattedValue, validations, reset, 'tel']);
	};

	return (
		<>
			{(label || labelSpacing) && (
				<InputLabel
					className={`${cssprefix}label-spacer-${labelSpacing && !label ? labelSpacing : 'nn'}`}
					data-testid={`${name}-tel`}
					htmlFor={name}
				>
					{label || ' '}
				</InputLabel>
			)}
			<TextField
				variant="outlined"
				type="tel"
				name={name}
				id={name}
				placeholder={placeholder}
				onChange={handleChange}
				onBlur={() => publish('markInteracted', name)}
				defaultValue={formatUSTel((value as string) || '')}
				error={showError}
				helperText={<Typography variant="errorValidation">{errMsg}</Typography>}
				inputRef={inputRef}
				inputProps={flattenIntoKey(tagData, 'data')}
			/>
		</>
	);
}
